'use strict';

/**
 * Single source of truth for the Sentry `release` identifier.
 *
 * MUST be called by BOTH the runtime (App.js -> Sentry.init) and the OTA
 * sourcemap-upload script. The runtime only knows `Updates.updateId`; the
 * upload side derives the identical string from the EAS update id returned by
 * `eas update --json`. If these ever diverge, symbolication silently breaks.
 *
 * Authored as CommonJS (not ESM like sibling utils) so the Node build scripts
 * can require() it directly while Metro still bundles it for the app.
 *
 * @param {Object} params
 * @param {string} params.version   expo.version from app.json, e.g. "6.9"
 * @param {string|null|undefined} params.updateId  EAS update UUID; null/undefined on embedded builds
 * @param {string} params.platform  "ios" | "android" | "web"
 * @returns {string} e.g. "6.9-019e930-ios", "6.9-android", or "6.9"
 */
function buildSentryRelease({ version, updateId, platform }) {
  let releaseVersion = version;
  if (platform !== 'web' && updateId) {
    releaseVersion = `${version}-${updateId.slice(0, 7)}`;
  }
  return platform === 'web' ? releaseVersion : `${releaseVersion}-${platform}`;
}

/**
 * Single source of truth for the Sentry `dist` identifier — the OTHER half of the same contract,
 * and until ASO-AN-003 the half nothing enforced.
 *
 * `buildSentryRelease` exists because a runtime release string and an upload release string that
 * drift apart break symbolication silently. `dist` is looked up alongside `release` when Sentry
 * resolves an artifact, so it has exactly the same property — and it was written twice, as two
 * independent literals: `dist: Platform.OS` in App.js and `--dist <platform>` in publish-ota.js.
 * Two literals that must agree forever is the shape the release helper was created to remove.
 *
 * It also carried no information: `dist = 'ios'` only repeats what the release suffix already
 * says, so the field named nothing at all.
 *
 * So:
 *
 *   updateId present  -> the EAS update id.
 *       Identifies the JS bundle, which is what a sourcemap actually belongs to. Runtime and
 *       uploader derive it from the same `updateId`, so symbolication is preserved BY
 *       CONSTRUCTION rather than by two people remembering.
 *
 *       MEASURE FIRST, then read this branch's reach: over 90 days of production sessions, 108 of
 *       109 Sentry releases are three-part, i.e. `Updates.updateId` is non-null on EMBEDDED
 *       binaries too (expo-updates gives the embedded manifest its own id — a UUIDv4, versus the
 *       UUIDv7 of a published OTA). So in the field this branch is effectively the only one that
 *       runs, and the release string was ALREADY distinguishing binaries before this change.
 *       What changes is that `dist` now names the artifact instead of duplicating the platform,
 *       and that both sides derive it from here. Do not claim more than that.
 *
 *   no updateId  -> the native build number.
 *       `ios.buildNumber` / `android.versionCode` from the bundled app.json. For an `eas build`
 *       these are the binary's own values: the `production` profile has `autoIncrement: true`,
 *       which writes them into app.json BEFORE the bundle is built, so the embedded copy is the
 *       binary's copy. Not observed in production (see above) — it is the correct answer for a
 *       build without expo-updates, and it costs nothing to be right about.
 *
 *   Web -> undefined, unchanged.
 *
 * Returns a string or undefined; NEVER an empty string, which Sentry would treat as a distinct
 * dist rather than as "none".
 *
 * @param {Object} params
 * @param {string} params.platform  "ios" | "android" | "web"
 * @param {string|null|undefined} params.updateId  EAS update UUID; null/undefined on embedded builds
 * @param {string|number|null|undefined} params.buildNumber   expo.ios.buildNumber
 * @param {string|number|null|undefined} params.versionCode   expo.android.versionCode
 * @returns {string|undefined} e.g. "019e930a-1111-7abc-8def-000000000000", "207", or undefined
 */
function buildSentryDist({ platform, updateId, buildNumber, versionCode }) {
  if (platform === 'web') return undefined;
  if (updateId) return String(updateId);

  const nativeBuild = platform === 'ios' ? buildNumber : versionCode;
  if (nativeBuild === null || nativeBuild === undefined || nativeBuild === '') return undefined;
  return String(nativeBuild);
}

module.exports = { buildSentryRelease, buildSentryDist };
